'use client';

import { WorkflowBookmark } from './workflow-bookmark';
import { useWorkflows } from '@/providers/workflow-provider';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { usePathname } from 'next/navigation';
import { Button } from '../ui/button';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useMemo } from 'react';

const HIDDEN_ON = ['my-workday', 'inbox/chat', 'workflows', 'bookmarks'];

export const CurrentWorkflowBanner = () => {
  const {
    userWorkflows,
    workEngagementWorkflows,
    currentWorkflowFolder,
    setCurrentWorkflowFolder,
    currentWorkflowBookmark,
    setCurrentWorkflowBookmark,
  } = useWorkflows();

  const pathname = usePathname();
  const isHidden = HIDDEN_ON.some((hiddenPath) =>
    pathname.includes(hiddenPath)
  );

  const currentFolderSelectValue = currentWorkflowFolder?.client_id;

  const handleFolderChange = (value: string) => {
    const folder = folders?.find((folder) => folder.client_id === value);
    if (folder) {
      setCurrentWorkflowFolder(folder);
    }
  };

  const handleClose = () => {
    setCurrentWorkflowFolder(null);
    setCurrentWorkflowBookmark(null);
  };

  const folders = useMemo(() => {
    return [
      ...(userWorkflows?.folders.filter(
        (folder) => folder.kind === 'workflow'
      ) ?? []),
      ...(workEngagementWorkflows?.flatMap((workflow) =>
        workflow.folders.filter((folder) => folder.kind === 'workflow')
      ) ?? []),
    ];
  }, [userWorkflows, workEngagementWorkflows]);

  if (
    isHidden ||
    !currentWorkflowFolder ||
    !currentWorkflowBookmark ||
    folders.length === 0
  )
    return null;

  const childBookmarks = currentWorkflowFolder.child_bookmarks;

  return (
    <div className='sticky top-0 z-20 flex w-full flex-row items-center justify-between border-b bg-background'>
      <div className='flex w-full overflow-x-auto p-2'>
        {childBookmarks?.map((bookmark, index, arr) => {
          return (
            <WorkflowBookmark
              key={bookmark.client_id}
              isEditMode={false}
              variant='banner'
              bookmark={bookmark}
              isLastItem={index === arr.length - 1}
            />
          );
        })}
      </div>
      <div className='flex flex-row gap-2 border-l p-2'>
        <Select
          value={currentFolderSelectValue}
          onValueChange={handleFolderChange}
        >
          <SelectTrigger
            disabled={!folders.length}
            className='h-[34px] min-h-0 w-[34px] justify-center p-0 [&>svg]:shrink-0 [&>svg]:text-foreground [&>svg]:opacity-100'
          />
          <SelectContent className='bg-background'>
            {folders.map((folder) => {
              return (
                <SelectItem
                  disabled={!folder.child_bookmarks?.length}
                  value={folder.client_id}
                  key={folder.client_id}
                >
                  <span className='block max-w-[300px] min-w-0 truncate'>
                    {folder.name}{' '}
                    {!folder.child_bookmarks?.length && (
                      <span className='text-xs text-muted-foreground'>
                        (Tomt flöde)
                      </span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Button
          className='h-[34px] w-[34px]'
          variant='outline'
          size='icon-sm'
          onClick={handleClose}
        >
          <XMarkIcon className='size-4' />
          <span className='sr-only'>Close</span>
        </Button>
      </div>
    </div>
  );
};
